import { FormattedTime } from 'react-intl';
import { Typography, Box, useTheme } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { keyframes } from '@emotion/react';
import { getToday } from 'utilities';

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0px rgba(167, 255, 167, 0.3);
  }
  50% {
    transform: scale(1.03);
    box-shadow: 0 0 12px rgba(120, 210, 120, 0.6);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0px rgba(167, 255, 167, 0.3);
  }
`;

export function TimeTypography({
  start,
  end,
  allDay = false,
  homepage = false,
  daysOfTheWeek = [],
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Theme-aware colors
  const colors = {
    background: isDark ? '#1a3d1f' : '#ddffdd',
    border: isDark ? '#2d5a33' : '#70be74',
    text: isDark ? '#81c784' : '#256029',
    pulseBackground: isDark ? '#1e4a23' : '#c8facc',
    pulseBorder: isDark ? '#388e3c' : '#70be74',
    barColor: isDark ? '#2e7d32' : '#a5d6a7',
  };

  const timeTypographySx = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '8px',
    fontWeight: 500,
    fontSize: '0.85rem',
    background: colors.background,
    border: `1px solid ${colors.border}`,
    color: colors.text,
    overflow: 'hidden',
    width: '100%'
  };

  const pulseSx = {
    ...timeTypographySx,
    backgroundColor: colors.pulseBackground,
    borderColor: colors.pulseBorder,
    animation: `${pulse} 2s infinite ease-in-out`,
    color: colors.text,
  };

  const renderContent = (label, barWidth, sx, barColor = colors.barColor) => (
    <Box sx={{ ...sx }}>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: `${barWidth}%`,
          backgroundColor: barColor,
          zIndex: 0,
        }}
      />
      <Typography
        component="span"
        variant="body2"
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          color: 'inherit',
        }}
      >
        <AccessTimeIcon 
          fontSize="small" 
          sx={{ 
            mr: 0.5, 
            verticalAlign: 'middle',
            color: 'inherit',
          }} 
        />
        {label}
      </Typography>
    </Box>
  );

  // If allDay or times are missing, show "All day"
  if (allDay || !start || !end) {
    return renderContent('All day', 100, timeTypographySx);
  }

  // Parse times safely
  const now = new Date();
  const selectedDay = getToday();

  const startParts = start.split(':');
  const endParts = end.split(':');

  // Validate time format
  if (startParts.length !== 2 || endParts.length !== 2) {
    return renderContent('All day', 100, timeTypographySx);
  }

  const [h1, m1] = startParts.map(Number);
  const [h2, m2] = endParts.map(Number);

  // Validate parsed numbers
  if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) {
    return renderContent('All day', 100, timeTypographySx);
  }

  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h1, m1);
  const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h2, m2);
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

  const totalActive = endDate - startDate;
  const elapsedActive = now - startDate;
  const percent = Math.min(100, Math.max(0, (elapsedActive / totalActive) * 100));

  const totalUntilStart = startDate - now;
  const totalPreWindow = startDate - midnight;
  const prePercent = 100 - Math.min(100, Math.max(0, (totalUntilStart / totalPreWindow) * 100));

  // Determine if today's day (or selected day) is listed for the deal
  const today = now.toLocaleDateString('en-US', { weekday: 'long' });
  const isTodayMatch =
    selectedDay.toLowerCase() === today.toLowerCase() && 
    daysOfTheWeek.map((d) => d.toLowerCase()).includes(today.toLowerCase());

  // Only show "Now!" if:
  // 1. Deal is for today
  // 2. And current time is within active window
  const isActiveNow = isTodayMatch && now >= startDate && now <= endDate;

  if (isActiveNow) {
    const formattedStart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(startDate);
    const formattedEnd = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(endDate);

    return renderContent(
      `Now! · ${formattedStart} - ${formattedEnd}`,
      percent,
      homepage ? pulseSx : timeTypographySx
    );
  }

  return renderContent(
    <>
      <FormattedTime value={startDate} hour="numeric" minute="2-digit" hour12 />
      {' – '}
      <FormattedTime value={endDate} hour="numeric" minute="2-digit" hour12 />
    </>,
    isTodayMatch ? prePercent : 0,
    timeTypographySx
  );
}